/**
 * Audio Recorder Mixin
 *
 * Provides audio recording functionality using MediaRecorder API.
 *
 * Usage:
 *   import audioRecorder from '@/components/OliverBotV2/mixins/audioRecorder.js'
 *
 *   export default {
 *     mixins: [audioRecorder],
 *     methods: {
 *       handleMicClick() {
 *         if (this.isRecording) {
 *           this.stopRecording();
 *         } else {
 *           this.startRecording();
 *         }
 *       }
 *     }
 *   }
 *
 * Events Emitted:
 *   - 'audio-recorded': Emitted when recording is complete with audioFile object
 *   - 'recording-started': Emitted when recording begins
 *   - 'recording-stopped': Emitted when recording stops
 *   - 'recording-error': Emitted when an error occurs with error object
 */
export default {
  data() {
    return {
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
    };
  },

  methods: {
    /**
     * Start recording audio from the microphone
     */
    async startRecording() {
      // Check if component has disabled prop and respect it
      if (this.disabled) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];

        this.mediaRecorder.addEventListener("dataavailable", (event) => {
          this.audioChunks.push(event.data);
        });

        this.mediaRecorder.addEventListener("stop", async () => {
          const audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });
          await this.handleAudioRecorded(audioBlob);

          // Stop all tracks to release the microphone
          stream.getTracks().forEach((track) => track.stop());
        });

        this.mediaRecorder.start();
        this.isRecording = true;
        this.$emit("recording-started");
      } catch (error) {
        console.error("Error accessing microphone:", error);
        this.$emit("recording-error", error);

        let title = "Microphone Unavailable";
        let text;

        if (
          error.name === "NotAllowedError" ||
          error.name === "PermissionDeniedError"
        ) {
          title = "Microphone Access Denied";
          text =
            "Microphone permission has been blocked. To enable voice input, allow microphone access in your browser settings and reload the page.";
        } else if (
          error.name === "NotFoundError" ||
          error.name === "DevicesNotFoundError"
        ) {
          text =
            "No microphone was found. Please connect a microphone and try again.";
        } else if (
          error.name === "NotReadableError" ||
          error.name === "TrackStartError"
        ) {
          text =
            "Your microphone is already in use by another application. Please close other apps using the microphone and try again.";
        } else {
          text =
            "Unable to access the microphone. Please check your device settings and try again.";
        }

        this.$swal.fire({
          icon: "warning",
          title,
          text,
          confirmButtonText: "OK",
        });
      }
    },

    /**
     * Stop the current recording
     */
    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;
        this.$emit("recording-stopped");
      }
    },

    /**
     * Handle the recorded audio blob
     * @param {Blob} blob - The recorded audio blob
     */
    async handleAudioRecorded(blob) {
      const audioFile = {
        blob,
        name: `recording-${Date.now()}.webm`,
        type: blob.type,
      };

      // Emit event for parent components
      this.$emit("audio-recorded", audioFile);

      // Call component-specific handler if it exists
      if (this.onAudioRecorded && typeof this.onAudioRecorded === "function") {
        await this.onAudioRecorded(audioFile);
      }
    },
  },
};
